'use client';

import { useTranslations } from 'next-intl';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
} from '@/shared/ui';
import { useGoalsEditor } from '@/features/project/hooks/use-goals-editor';
import { BriefTextForm } from '@/features/project/ui/BriefTextForm';
import { BriefMaterials } from '@/features/angles-overview';

interface ProjectGoalsSheetProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * NestedSheet with the WHOLE brief: the source materials grid (views,
 * sketches, documents, references) followed by the text form (goals +
 * construction region + facade area). Both halves are the very components the
 * standalone brief page renders, so the panel and the page cannot drift apart
 * in what they show or what they can do. Shared by the render-settings goals
 * row and the estimate editor's facade-area link. Mirrors Apple's
 * `ProjectGoalsView` sheet and Android's `ProjectGoalsPanelContent`.
 *
 * No save button and no "save changes?" prompt: closing the sheet saves any
 * unsaved edit (the sheet stays mounted while closed, so the editor's unmount
 * save does not cover it). Buffer and save model live in `useGoalsEditor`.
 */
export function ProjectGoalsSheet({
  projectId,
  open,
  onOpenChange,
}: ProjectGoalsSheetProps) {
  const t = useTranslations('projectGoals');

  // The buffer is owned here and handed to the shared `BriefTextForm`.
  const editor = useGoalsEditor(projectId);
  const { project, save } = editor;
  const isArchived = !!project?.attributes.archived_at;

  return (
      <NestedSheet
        open={open}
        onOpenChange={(next) => {
          // Save BEFORE the host's close handler: hosts that await the
          // coordinator on close (estimate) then wait for this very PATCH.
          if (!next) save();
          onOpenChange(next);
        }}
      >
        <NestedSheetContent>
          <NestedSheetHeader>
            <NestedSheetTitle>{t('label')}</NestedSheetTitle>
          </NestedSheetHeader>

          <NestedSheetBody className="px-4">
            <div className="space-y-6 py-2">
              {/* Source materials — the same grid the brief page shows, with the
                  same uploading, sketching and detail affordances. Without it
                  the panel read as a poorer, text-only brief and hid the very
                  photos the render runs on. Files lead, the text form follows
                  (the narrow-column order of the brief page). */}
              <BriefMaterials projectId={projectId} isArchived={isArchived} />

              <BriefTextForm
                editor={editor}
                idPrefix="render"
                isArchived={isArchived}
              />
            </div>
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>
  );
}
